/* Rules and where each one stands, as the redesign lays them out.
 *
 * **The lifecycle strip comes first, above the tabs**, because it is the answer to the question this
 * screen is opened with most — "why is my rule not running" — and it answers it for every rule at
 * once: a rule runs only once a published version has two signatures from people other than its
 * author. The five steps carry how many rules are in each and filter the table.
 *
 * **One read for the table**, `GET /v1/rules/overview`: each rule beside its latest version, the
 * dimension it declares, the most severe of its live bindings, its open activation request and when
 * it last changed. Asking for those row by row is the fan-out that once exhausted the API's
 * connection pool.
 *
 * **Opening a rule opens a panel over the list rather than a page.** The address still names the rule
 * — `/rules/:ruleId` renders this screen with the panel open — so every existing link to a rule keeps
 * working, and the next rule is one click away rather than a Back and a click.
 *
 * **The Bindings tab answers "does this run without me"**: each binding's selector and column, its
 * severity, whether it is on, and when it runs — the Runs control opens its schedule and its harvest
 * trigger. Unbinding asks first, because it stops a rule measuring a dataset, and says it is not a
 * delete.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { CREATE_RULE_PERMISSION, can, useIdentity } from '@/entities/session';
import { useConnections } from '@/features/connections';
import { useRuleDraft } from '@/features/rulegen';
import {
  BindingScheduling,
  runsLabel,
  useBindingScheduling,
} from '@/features/scheduling';
import type { SchedulingSummary } from '@/shared/api/generated/rules';
import { ageText } from '@/shared/model/age';
import { SCREEN_PATHS, rulePrefillFrom } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { SidePanel } from '@/shared/ui/SidePanel';
import { Tabs } from '@/shared/ui/Tabs';

import { useBindings, useDisableBinding, useRuleOverview } from '../api/rules';
import type { RuleBinding, RuleOverview, Selector } from '../api/rules';
import {
  PHASES,
  PHASE_TONE,
  RUN_FILTERS,
  approvalOf,
  matchesRunFilter,
  phaseOf,
  runModesOf,
} from '../model/overview';
import type { RulePhase, RunFilter } from '../model/overview';
import { shortRunId } from '../model/presentation';

import { RuleDrawer } from './RuleDrawer';
import { BindRuleForm } from './BindRuleForm';
import { CreateRuleForm } from './RuleForms';
import { RunBindingButton } from './RunBindingButton';

type RulesPane = 'rules' | 'bindings';

/* What each phase means, on hover over its step. The three rule states and the two gates already
 * carry these sentences elsewhere on this screen, so they are named rather than written again. */
const PHASE_MEANING: Readonly<Record<RulePhase, string>> = {
  draft: 'rules.state.draft.meaning',
  published: 'rules.stage.published.meaning',
  awaiting: 'rules.stage.awaitingApproval.meaning',
  active: 'rules.state.active.meaning',
  deprecated: 'rules.state.deprecated.meaning',
};

export function RulesScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { ruleId } = useParams<{ ruleId: string }>();
  const identity = useIdentity();
  const overview = useRuleOverview();
  const [pane, setPane] = useState<RulesPane>('rules');
  const [phase, setPhase] = useState<RulePhase | null>(null);
  const [creating, setCreating] = useState(false);
  const [binding, setBinding] = useState(false);

  /* **A suggestion arriving from `/suggestions`, or `null` for an ordinary visit.** Read once on
     mount: a reader who closes the form and opens it again is authoring by hand, and re-seeding
     from the history entry would put the suggestion back into a form they had just cleared. The
     form opens once the draft has resolved (or failed), so its fields are seeded at mount rather
     than rendering blank — and only once, or a closed form would reopen on the next render. */
  const [prefill] = useState(() => rulePrefillFrom(location.state));
  const draft = useRuleDraft(prefill?.suggestionId);
  const [offered, setOffered] = useState(false);
  useEffect(() => {
    if (offered || prefill === null) return;
    if (draft.isSuccess || draft.isError) {
      setOffered(true);
      setCreating(true);
    }
  }, [offered, prefill, draft.isSuccess, draft.isError]);

  /* Three states, not two: `can` denies while the identity read is in flight, and a read-only
     band claimed from an unanswered request would be a claim about somebody's role with no grounds.
     Nothing is said until it answers. */
  const standing: 'unknown' | 'author' | 'reader' =
    identity.isPending || identity.isError
      ? 'unknown'
      : can(identity.data, CREATE_RULE_PERMISSION)
        ? 'author'
        : 'reader';

  const rows = useMemo(() => overview.data?.items ?? [], [overview.data]);
  const byId = useMemo(() => new Map(rows.map((row) => [row.rule.id, row])), [rows]);

  const closePanel = (): void => {
    setCreating(false);
    if (ruleId !== undefined) navigate(SCREEN_PATHS.rules);
  };

  return (
    <div className="lq-ru">
      <PageHeader
        title={t('rules.title')}
        lede={t('rules.lede')}
        help={t('help.feature.rules')}
        actions={
          <button
            type="button"
            className="lq-ru__author"
            disabled={standing !== 'author'}
            title={standing === 'reader' ? t('rules.authorRefused') : undefined}
            onClick={() => {
              if (ruleId !== undefined) navigate(SCREEN_PATHS.rules);
              setCreating(true);
            }}
          >
            {t('rules.create.open')}
          </button>
        }
      />

      {standing === 'reader' ? (
        <p className="lq-tintband lq-tintband--muted">
          <span className="lq-tintband__text">
            <strong>{t('rules.readOnly.title')}</strong> {t('rules.readOnly.body')}
          </span>
        </p>
      ) : null}

      <PhaseStrip
        rows={overview.isSuccess ? rows : null}
        filter={phase}
        onFilter={(next) => {
          setPhase(next);
          setPane('rules');
        }}
      />

      <Tabs
        label={t('rules.tabs.label')}
        active={pane}
        onSelect={setPane}
        tabs={[
          { id: 'rules', label: t('rules.tab.rules') },
          { id: 'bindings', label: t('rules.tab.bindings') },
        ]}
      />

      {pane === 'rules' ? (
        <RulesTable
          overview={overview}
          rows={rows}
          phase={phase}
          selected={ruleId ?? null}
          onClearPhase={() => {
            setPhase(null);
          }}
          onOpen={(id) => {
            setCreating(false);
            navigate(`${SCREEN_PATHS.rules}/${id}`);
          }}
        />
      ) : (
        <BindingsPane
          byId={byId}
          onBind={() => {
            setBinding(true);
          }}
        />
      )}

      {ruleId !== undefined ? (
        <RuleDrawer ruleId={ruleId} row={byId.get(ruleId)} onClose={closePanel} />
      ) : creating ? (
        <SidePanel
          label={t('rules.create.title')}
          onClose={closePanel}
          head={
            <>
              <h2 className="lq-side__name">{t('rules.create.title')}</h2>
              <div className="lq-side__sub">{t('rules.create.hint')}</div>
            </>
          }
        >
          <CreateRuleForm
            draft={draft.isSuccess ? draft.data : undefined}
            steps={
              <ol
                className="lq-ru__createSteps"
                aria-label={t('rules.create.stepsLabel')}
              >
                <li>
                  <span className="lq-ru__createN">1</span>
                  {t('rules.create.step1')}
                </li>
                <li>
                  <span className="lq-ru__createN">2</span>
                  {t('rules.create.step2')}
                </li>
                <li>
                  <span className="lq-ru__createN">3</span>
                  {t('rules.create.step3')}
                </li>
              </ol>
            }
            onCreated={(rule) => {
              setCreating(false);
              /* Straight to the rule just created: publishing its first version is the very next
                 step, and it is in that rule's panel. */
              navigate(`${SCREEN_PATHS.rules}/${rule.id}`);
            }}
            onCancel={() => {
              setCreating(false);
            }}
          />
        </SidePanel>
      ) : null}

      <Modal
        open={binding}
        title={t('rules.bindings.create.title')}
        hint={t('rules.bindings.create.datasetHint')}
        className="lq-bindm"
        onClose={() => {
          setBinding(false);
        }}
      >
        <BindRuleForm
          onCreated={() => {
            setBinding(false);
          }}
          onCancel={() => {
            setBinding(false);
          }}
        />
      </Modal>
    </div>
  );
}

/** The five steps a rule travels, as cards. Each carries how many rules are at it and filters the
 * table; the two gates are dashed, because a rule passes through them rather than resting in one. */
function PhaseStrip({
  rows,
  filter,
  onFilter,
}: {
  /** `null` until the overview has answered — a count before then would be invented. */
  readonly rows: readonly RuleOverview[] | null;
  readonly filter: RulePhase | null;
  readonly onFilter: (phase: RulePhase | null) => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="lq-ru__strip" role="group" aria-label={t('rules.lifecycle.label')}>
      {PHASES.map((step, index) => {
        const count =
          rows === null ? null : rows.filter((row) => phaseOf(row) === step.phase).length;
        return (
          <div key={step.phase} className="lq-ru__stepWrap">
            <button
              type="button"
              className={step.gate ? 'lq-ru__phase lq-ru__phase--gate' : 'lq-ru__phase'}
              aria-pressed={filter === step.phase}
              title={t(PHASE_MEANING[step.phase])}
              onClick={() => {
                onFilter(filter === step.phase ? null : step.phase);
              }}
            >
              <span className="lq-ru__phaseHead">
                <span className="lq-ru__phaseN">{count ?? '—'}</span>
                <span className="lq-ru__phaseLabel">
                  {t(`rules.phase.${step.phase}.label`)}
                </span>
              </span>
              <span className="lq-ru__phaseSub">
                {step.gate
                  ? t(`rules.phase.${step.phase}.gateSub`)
                  : t('rules.lifecycle.state')}
              </span>
            </button>
            {index < PHASES.length - 1 ? (
              <span className="lq-ru__arrow" aria-hidden="true">
                →
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function RulesTable({
  overview,
  rows,
  phase,
  selected,
  onClearPhase,
  onOpen,
}: {
  readonly overview: ReturnType<typeof useRuleOverview>;
  readonly rows: readonly RuleOverview[];
  readonly phase: RulePhase | null;
  readonly selected: string | null;
  readonly onClearPhase: () => void;
  readonly onOpen: (ruleId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  /* One clock for the table, so two rules changed in the same minute read the same age. */
  const [now] = useState(() => new Date());
  const shown = phase === null ? rows : rows.filter((row) => phaseOf(row) === phase);

  if (overview.isPending) return <SkeletonRows rows={6} columns={7} />;
  if (overview.isError) {
    return (
      <Alert tone="error" title={t('rules.unreachable')}>
        {overview.error.message}
      </Alert>
    );
  }

  return (
    <>
      {phase === null ? null : (
        <p className="lq-ru__filtered">
          {t('rules.phase.showing', { phase: t(`rules.phase.${phase}.label`) })}{' '}
          <button type="button" className="lq-ru__showAll" onClick={onClearPhase}>
            {t('rules.lifecycle.showAll')}
          </button>
        </p>
      )}
      <section
        className="lq-cp__card lq-cp__card--table"
        aria-label={t('rules.tableCaption')}
      >
        <div className="lq-cp__scroll">
          <table className="lq-table lq-table--ledger lq-cp__table lq-ru__table">
            <caption className="lq-visually-hidden">{t('rules.tableCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('rules.column.rule')}</th>
                <th scope="col">{t('rules.column.kind')}</th>
                <th scope="col">{t('rules.column.dimension')}</th>
                <th scope="col">{t('rules.column.severity')}</th>
                <th scope="col">
                  <HelpLabel
                    subject={t('rules.column.state')}
                    help={t('help.field.ruleState')}
                  >
                    <span>{t('rules.column.state')}</span>
                  </HelpLabel>
                </th>
                <th scope="col">{t('rules.column.approval')}</th>
                <th scope="col">{t('rules.column.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <RuleRow
                  key={row.rule.id}
                  row={row}
                  now={now}
                  selected={row.rule.id === selected}
                  onOpen={() => {
                    onOpen(row.rule.id);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
        {shown.length === 0 ? (
          /* Two sentences: an empty estate is about the tenant, an empty step about the filter. */
          <p className="lq-ru__none">
            {rows.length === 0 ? t('rules.emptyLine') : t('rules.filteredNone')}
          </p>
        ) : null}
      </section>
    </>
  );
}

function RuleRow({
  row,
  now,
  selected,
  onOpen,
}: {
  readonly row: RuleOverview;
  readonly now: Date;
  readonly selected: boolean;
  readonly onOpen: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const phase = phaseOf(row);
  const approval = approvalOf(row);

  return (
    <tr
      className={selected ? 'lq-ru__row lq-ru__row--on' : 'lq-ru__row'}
      onClick={onOpen}
    >
      <td data-label={t('rules.column.rule')}>
        {/* The name is the control, so a keyboard reader has one stop per rule; the row's own
            click is the pointer's shortcut to the same place. */}
        <button
          type="button"
          className="lq-ru__name"
          aria-current={selected ? 'true' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          {row.rule.name}
        </button>
        {row.latest_version === null ? null : (
          <span className="lq-ru__ver"> v{row.latest_version}</span>
        )}
      </td>
      <td
        data-label={t('rules.column.kind')}
        className="lq-ru__kind"
        title={t(`rules.kind.${row.rule.kind}`)}
      >
        {row.rule.kind}
      </td>
      <td data-label={t('rules.column.dimension')} className="lq-ru__dim">
        {row.dimension === null ? (
          <span className="lq-muted">—</span>
        ) : (
          t(`scoring.dimension.${row.dimension}`)
        )}
      </td>
      <td data-label={t('rules.column.severity')} className="lq-ru__dim">
        {/* A rule has no severity of its own; its live bindings carry one. An unbound rule has none,
            which a dash says and a default would not. */}
        {row.severity === null ? (
          <span className="lq-muted" title={t('rules.noSeverity')}>
            —
          </span>
        ) : (
          t(`rules.severity.${row.severity}.label`)
        )}
      </td>
      <td data-label={t('rules.column.state')}>
        <span
          className={`lq-tag lq-tag--ru-${PHASE_TONE[phase]}`}
          title={t(PHASE_MEANING[phase])}
        >
          {t(`rules.phase.${phase}.pill`)}
        </span>
      </td>
      <td data-label={t('rules.column.approval')} className="lq-ru__approval">
        {approval.kind === 'collecting' ? (
          t('rules.approvalCell.collecting', {
            collected: approval.collected,
            required: approval.required,
          })
        ) : approval.kind === 'ready' ? (
          <span className="lq-ru__ready">{t('rules.approvalCell.ready')}</span>
        ) : null}
      </td>
      <td data-label={t('rules.column.updated')} className="lq-ru__when">
        {row.updated_at === null ? (
          '—'
        ) : (
          <span title={row.updated_at}>
            {ageText(t, row.updated_at, now) ?? row.updated_at}
          </span>
        )}
      </td>
    </tr>
  );
}

/* ---------------------------------------------------------------------------- bindings */

function BindingsPane({
  byId,
  onBind,
}: {
  readonly byId: ReadonlyMap<string, RuleOverview>;
  readonly onBind: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const bindings = useBindings();
  const [filter, setFilter] = useState<RunFilter>('all');
  const [queued, setQueued] = useState<{
    readonly execution: { readonly id: string };
    readonly binding: RuleBinding;
  } | null>(null);
  const [scheduling, setScheduling] = useState<RuleBinding | null>(null);
  const [unbinding, setUnbinding] = useState<RuleBinding | null>(null);
  const disable = useDisableBinding();

  const items = useMemo(() => bindings.data?.items ?? [], [bindings.data]);
  /* Scheduling for the whole page in one read. A cell that fetched its own would be two requests a
     row, which is what exhausted the API's connection pool before. */
  const summaries = useBindingScheduling(items.map((each) => each.id));
  const summaryOf = (id: string): SchedulingSummary | undefined =>
    summaries.isSuccess ? summaries.data.get(id) : undefined;
  const shown = items.filter((each) =>
    matchesRunFilter(runModesOf(summaryOf(each.id)), filter),
  );
  const ruleName = (each: RuleBinding): string | null =>
    byId.get(each.rule_id)?.rule.name ?? null;

  return (
    <>
      {queued === null ? null : (
        <div className="lq-tintband lq-tintband--info lq-ru__queued" role="status">
          <span className="lq-ru__spin" aria-hidden="true" />
          <span className="lq-tintband__text">
            <strong>
              {t('rules.bindings.queued.requested', {
                run: shortRunId(queued.execution.id),
              })}
            </strong>{' '}
            {t('rules.bindings.queued.covers')}
          </span>
          <Link
            className="lq-ru__small"
            to={`${SCREEN_PATHS.executions}/${queued.execution.id}`}
          >
            {t('rules.bindings.queued.open')}
          </Link>
          <button
            type="button"
            className="lq-ru__dismiss"
            onClick={() => {
              setQueued(null);
            }}
          >
            {t('rules.bindings.queued.dismiss')}
          </button>
        </div>
      )}

      <div className="lq-ru__bindBar">
        <div
          className="lq-ru__seg"
          role="group"
          aria-label={t('rules.bindings.filterLabel')}
        >
          {RUN_FILTERS.map((each) => (
            <button
              key={each}
              type="button"
              className="lq-ru__segButton"
              aria-pressed={filter === each}
              onClick={() => {
                setFilter(each);
              }}
            >
              {t(`rules.bindings.filter.${each}`)}
            </button>
          ))}
        </div>
        <p className="lq-ru__bindNote">{t('rules.bindings.note')}</p>
        <button type="button" className="lq-ru__bind" onClick={onBind}>
          {t('rules.bindings.create.open')}
        </button>
      </div>

      {bindings.isPending ? <SkeletonRows rows={4} columns={7} /> : null}
      {bindings.isError ? (
        <Alert tone="error" title={t('rules.unreachable')}>
          {bindings.error.message}
        </Alert>
      ) : null}

      {bindings.isSuccess ? (
        <section
          className="lq-cp__card lq-cp__card--table"
          aria-label={t('rules.bindings.tableCaption')}
        >
          <div className="lq-cp__scroll">
            <table className="lq-table lq-table--ledger lq-cp__table lq-ru__bindings">
              <caption className="lq-visually-hidden">
                {t('rules.bindings.tableCaption')}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('rules.bindings.column.rule')}</th>
                  <th scope="col">{t('rules.bindings.column.selector')}</th>
                  <th scope="col">{t('rules.bindings.column.column')}</th>
                  <th scope="col">{t('rules.bindings.column.severity')}</th>
                  <th scope="col">{t('rules.bindings.column.standing')}</th>
                  <th scope="col">
                    <HelpLabel
                      subject={t('rules.bindings.column.runs')}
                      help={t('help.field.bindingScheduling')}
                    >
                      <span>{t('rules.bindings.column.runs')}</span>
                    </HelpLabel>
                  </th>
                  <th scope="col" className="lq-table__actions">
                    <span className="lq-visually-hidden">
                      {t('rules.bindings.column.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((each) => (
                  <BindingRow
                    key={each.id}
                    binding={each}
                    rule={byId.get(each.rule_id)}
                    summary={summaryOf(each.id)}
                    live={queued?.binding.id === each.id}
                    onQueued={(execution) => {
                      setQueued({ execution, binding: each });
                    }}
                    onSchedule={() => {
                      setScheduling(each);
                    }}
                    onUnbind={() => {
                      setUnbinding(each);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 0 ? (
            <p className="lq-ru__none">
              {items.length === 0
                ? t('rules.bindings.empty')
                : t('rules.bindings.noneMatch')}
            </p>
          ) : null}
        </section>
      ) : null}

      {scheduling === null ? null : (
        <SchedulingDialog
          binding={scheduling}
          onClose={() => {
            setScheduling(null);
          }}
        />
      )}

      <ConfirmDialog
        open={unbinding !== null}
        title={t('rules.bindings.unbindConfirm.title')}
        message={
          unbinding === null
            ? ''
            : t('rules.bindings.unbindConfirm.body', {
                rule: ruleName(unbinding) ?? t('rules.bindings.ruleUnknown'),
                target: selectorText(unbinding.selectors),
              })
        }
        confirmLabel={t('rules.bindings.unbind')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={disable.isPending}
        error={disable.isError ? disable.error.message : undefined}
        onConfirm={() => {
          if (unbinding === null) return;
          disable.mutate(
            { bindingId: unbinding.id, reason: 'Unbound from the Rules screen' },
            {
              onSuccess: () => {
                setUnbinding(null);
              },
            },
          );
        }}
        onCancel={() => {
          setUnbinding(null);
          disable.reset();
        }}
      />
    </>
  );
}

/** A selector as the design writes it, "name: gold.*" — the kind quiet, the value in mono. */
function selectorValue(selector: Selector): string {
  return selector.pattern ?? selector.tag ?? selector.role ?? selector.description;
}

function selectorText(selectors: readonly Selector[]): string {
  return selectors.map(selectorValue).join(', ');
}

function BindingRow({
  binding,
  rule,
  summary,
  live,
  onQueued,
  onSchedule,
  onUnbind,
}: {
  readonly binding: RuleBinding;
  /** `undefined` when the overview's page did not include this binding's rule. */
  readonly rule: RuleOverview | undefined;
  readonly summary: SchedulingSummary | undefined;
  /** The row whose run was just queued, so the band above has a row to point at. */
  readonly live: boolean;
  readonly onQueued: (execution: { readonly id: string }) => void;
  readonly onSchedule: () => void;
  readonly onUnbind: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const modes = runModesOf(summary);

  /* Whether this binding runs: off is off, whatever the rule says; on behind a rule that is not
     active is waiting on that rule, which is the commonest "why does nothing run". */
  const state: 'enabled' | 'disabled' | 'waiting' = !binding.enabled
    ? 'disabled'
    : rule !== undefined && rule.rule.state !== 'active'
      ? 'waiting'
      : 'enabled';

  const connections = useConnections();
  const runs =
    summary === undefined
      ? { short: t('rules.runs.unread'), full: t('rules.runs.unread') }
      : runsLabel(
          t,
          summary,
          (id) => connections.data?.items.find((each) => each.id === id)?.name ?? null,
        );
  const unattended = modes !== null && (modes.scheduled || modes.afterHarvest);

  return (
    <tr className={live ? 'lq-ru__brow lq-ru__brow--live' : 'lq-ru__brow'}>
      <td data-label={t('rules.bindings.column.rule')} className="lq-ru__brule">
        {rule?.rule.name ?? (
          <span className="lq-muted">{t('rules.bindings.ruleUnknown')}</span>
        )}
      </td>
      <td data-label={t('rules.bindings.column.selector')}>
        <span
          className="lq-ru__selector"
          title={binding.selectors.map((s) => s.description).join(' or ')}
        >
          {binding.selectors.map((selector, index) => (
            <span key={`${selector.kind}-${String(index)}`}>
              {index > 0 ? ', ' : null}
              <span className="lq-ru__selKind">
                {t(`rules.selector.${selector.kind}`)}:
              </span>{' '}
              {selectorValue(selector)}
            </span>
          ))}
        </span>
      </td>
      <td data-label={t('rules.bindings.column.column')} className="lq-ru__col">
        {binding.explicit_columns.length === 0 ? (
          <span className="lq-muted" title={t('rules.bindings.everyColumnMeaning')}>
            {t('rules.bindings.everyColumn')}
          </span>
        ) : (
          binding.explicit_columns.join(', ')
        )}
      </td>
      <td data-label={t('rules.bindings.column.severity')} className="lq-ru__dim">
        {t(`rules.severity.${binding.severity}.label`)}
      </td>
      <td data-label={t('rules.bindings.column.standing')}>
        <span className={`lq-tag lq-tag--ru-b${state}`}>
          <span className="lq-tag__dot" aria-hidden="true" />
          {state === 'waiting' && rule !== undefined
            ? t(`rules.bindings.state.rule.${rule.rule.state}`)
            : t(`rules.bindings.state.${state}`)}
        </span>
      </td>
      <td data-label={t('rules.bindings.column.runs')}>
        <button
          type="button"
          className={unattended ? 'lq-ru__runs lq-ru__runs--on' : 'lq-ru__runs'}
          title={`${runs.full} — ${t('rules.runs.edit')}`}
          aria-label={`${runs.short}. ${t('rules.runs.edit')}`}
          onClick={onSchedule}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
            <path
              d="M12 7.5V12l3 2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span className="lq-ru__runsText">{runs.short}</span>
        </button>
      </td>
      <td className="lq-table__actions">
        {/* Run before Unbind: running is what a steward does repeatedly and unbinding is what they
            do once, and the destructive-sounding control first is how it gets pressed by mistake. */}
        {binding.enabled ? (
          <span className="lq-ru__bactions">
            <RunBindingButton bindingId={binding.id} enabled onQueued={onQueued} />
            <button
              type="button"
              className="lq-ru__small lq-ru__small--danger"
              onClick={onUnbind}
            >
              {t('rules.bindings.unbind')}
            </button>
          </span>
        ) : null}
      </td>
    </tr>
  );
}

/** When one binding runs, asked as one question.
 *
 * Its own component so the read it needs — the sources a harvest trigger can follow — is made only
 * when somebody opens it, rather than on every visit to the list. */
function SchedulingDialog({
  binding,
  onClose,
}: {
  readonly binding: RuleBinding;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const sources = useConnections();

  return (
    <Modal open title={t('scheduling.when.title')} onClose={onClose} className="lq-whenm">
      <BindingScheduling
        bindingId={binding.id}
        connections={(sources.data?.items ?? []).map((each) => ({
          id: each.id,
          name: each.name,
        }))}
        onDone={onClose}
      />
    </Modal>
  );
}
